import * as React from "react";
import type { Ref } from "react";
import { Link, type LinkProps } from "react-router-dom";

import { faArrowUpRightFromSquare, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
	type DropdownMenuArrowProps,
	type DropdownMenuCheckboxItemProps,
	type DropdownMenuContentProps,
	type DropdownMenuItemProps,
	type DropdownMenuLabelProps,
	type DropdownMenuPortalProps,
	type DropdownMenuProps,
	type DropdownMenuRadioGroupProps,
	type DropdownMenuRadioItemProps,
	type DropdownMenuSeparatorProps,
	type DropdownMenuSubContentProps,
	type DropdownMenuSubProps,
	type DropdownMenuSubTriggerProps,
	type DropdownMenuTriggerProps,
} from "@radix-ui/react-dropdown-menu";
import { Slot } from "@radix-ui/react-slot";
import cx from "classnames";
import { Emoji } from "emoji-mart";
import { noop } from "lodash";

import { useCurrentFullscreenElement } from "~/common/Fullscreen";
import { useMedia } from "~/lib/hooks";
import { Checkbox } from "~/ui";
import Avatar, { type AccountAttrs, AccountAvatar, type AvatarUserAttrs } from "~/ui/avatar";
import OldAvatar, { type AvatarProps as OldAvatarProps } from "~/ui/legacy/Avatar";
import { Modal, Switch } from "~/ui/legacyFellowIndex";

import styles from "./menu.module.css";

interface MobileModalData {
	showModalInsteadOfMenu: boolean;
	modalTitle: string;
	isOpen: boolean;
	openModal: () => void;
	closeModal: () => void;
}
const MobileModalContext = React.createContext<MobileModalData>({
	showModalInsteadOfMenu: false,
	modalTitle: "😶‍🌫️",
	isOpen: false,
	openModal: noop,
	closeModal: noop,
});
MobileModalContext.displayName = "MobileModalContext";

interface MenuProps extends DropdownMenuProps {
	/** When specified, touch-friendly modal is shown on screens with imprecise pointers (like phones) */
	mobileHeaderTitle?: string;
}

export function Menu({ mobileHeaderTitle, ...props }: MenuProps) {
	const { children, modal, ...rest } = props;

	const noCursor = useMedia(`(pointer: coarse)`);
	const showModalInsteadOfMenu = noCursor && Boolean(mobileHeaderTitle);
	const [isOpen, setIsOpen] = React.useState(false);
	const openModal = React.useCallback(() => setIsOpen(true), []);
	const closeModal = React.useCallback(() => setIsOpen(false), []);

	const modalItemsValue = React.useMemo<MobileModalData>(
		() => ({
			showModalInsteadOfMenu,
			modalTitle: mobileHeaderTitle ?? "🥷🏿",
			isOpen,
			openModal,
			closeModal,
		}),
		[showModalInsteadOfMenu, mobileHeaderTitle, isOpen, openModal, closeModal],
	);

	if (showModalInsteadOfMenu)
		return <MobileModalContext.Provider value={modalItemsValue}>{children}</MobileModalContext.Provider>;

	return (
		<DropdownMenuPrimitive.Root {...rest} modal={modal ?? false}>
			{children}
		</DropdownMenuPrimitive.Root>
	);
}
Menu.Root = Menu;

const Trigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>((props, ref) => {
	const { asChild = true, children, className, ...rest } = props;
	const { showModalInsteadOfMenu, openModal } = React.useContext(MobileModalContext);

	if (showModalInsteadOfMenu) {
		const Component = asChild ? Slot : "button";
		return (
			<Component {...rest} ref={ref} className={cx(styles.trigger, className)} onClick={openModal}>
				{children}
			</Component>
		);
	}

	return (
		<DropdownMenuPrimitive.Trigger {...rest} asChild={asChild} className={cx(styles.trigger, className)} ref={ref}>
			{children}
		</DropdownMenuPrimitive.Trigger>
	);
});

Trigger.displayName = "Trigger";
Menu.Trigger = Trigger;

function Portal(props: DropdownMenuPortalProps) {
	return <DropdownMenuPrimitive.Portal {...props}>{props.children}</DropdownMenuPrimitive.Portal>;
}
Menu.Portal = Portal;

type ContentProps = DropdownMenuContentProps & { portalContainer?: HTMLElement };

const Content = React.forwardRef(function Content(props: ContentProps, forwardedRef: Ref<HTMLDivElement>) {
	const { align, children, className, sideOffset, portalContainer, ...rest } = props;
	const { showModalInsteadOfMenu, modalTitle, isOpen, closeModal } = React.useContext(MobileModalContext);
	const currentFullscreenElement = useCurrentFullscreenElement();

	if (showModalInsteadOfMenu)
		return (
			<Modal isOpen={isOpen} onClose={closeModal}>
				<Modal.Header onClose={closeModal}>
					<Modal.Title>{modalTitle}</Modal.Title>
				</Modal.Header>
				<Modal.Body className={styles.mobile}>{children}</Modal.Body>
			</Modal>
		);

	return (
		<Menu.Portal container={portalContainer ?? currentFullscreenElement ?? undefined}>
			<DropdownMenuPrimitive.Content
				{...rest}
				data-ignore-focus-lock
				align={align ?? "start"}
				className={cx(styles.content, className)}
				sideOffset={sideOffset ?? 1}
				ref={forwardedRef}
			>
				{children}
			</DropdownMenuPrimitive.Content>
		</Menu.Portal>
	);
});
Menu.Content = Content;

type AdditionalItemProps = (
	| { icon?: IconDefinition; iconProps?: Omit<FontAwesomeIconProps, "icon"> }
	| { user?: AvatarUserAttrs }
	| { account?: AccountAttrs }
	| { team?: OldAvatarProps["team"] }
	| { emoji?: string | null }
) & {
	destructive?: boolean;
};

type WithAdditionalItemProps<T extends { onClick?: React.MouseEventHandler<HTMLElement> }> = Omit<T, "onClick"> & {
	/** @deprecated Radix relies on 'onSelect' for keyboard accessibility. You very very likely want that instead. -Wal*/
	onClick?: T["onClick"];
} & AdditionalItemProps;

export type MenuItemProps = WithAdditionalItemProps<DropdownMenuItemProps>;

const Item = React.forwardRef(function Item(props: MenuItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
	const { className, destructive, icon, iconProps, ...rest } =
		"icon" in props ? props : { icon: undefined, iconProps: undefined, ...props };
	const { disabled } = rest;

	const { showModalInsteadOfMenu, closeModal } = React.useContext(MobileModalContext);

	if (showModalInsteadOfMenu) {
		const { onSelect, ...others } = rest;

		return (
			<div
				ref={ref}
				{...others}
				onClick={event => {
					if (disabled) return;
					onSelect?.(event.nativeEvent);
					closeModal();
				}}
				className={cx(styles.item, className, {
					[styles.itemDestructive]: destructive,
					[styles.itemDisabled]: disabled,
				})}
			>
				{generateItem({ ...rest, icon, iconProps })}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.Item
			ref={ref}
			{...rest}
			className={cx(styles.item, className, { [styles.itemDestructive]: destructive })}
		>
			{generateItem({ ...rest, icon, iconProps })}
		</DropdownMenuPrimitive.Item>
	);
});
Menu.Item = Item;

type TriggerItemProps = WithAdditionalItemProps<DropdownMenuSubTriggerProps>;

function TriggerItem(props: TriggerItemProps) {
	const { className, destructive, icon, iconProps, ...rest } =
		"icon" in props ? props : { icon: undefined, iconProps: undefined, ...props };
	const { disabled } = rest;

	const { showModalInsteadOfMenu, openModal } = React.useContext(MobileModalContext);

	const itemContent = (
		<>
			{generateItem({ ...rest, icon, iconProps })}
			<FontAwesomeIcon fixedWidth icon={faChevronRight} className={styles.subTriggerIcon} />
		</>
	);

	if (showModalInsteadOfMenu) {
		return (
			<div
				onClick={openModal}
				className={cx(styles.item, className, {
					[styles.itemDestructive]: destructive,
					[styles.itemDisabled]: disabled,
				})}
			>
				{itemContent}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.SubTrigger
			{...rest}
			className={cx(styles.item, className, { [styles.itemDestructive]: destructive })}
		>
			{itemContent}
		</DropdownMenuPrimitive.SubTrigger>
	);
}
Menu.SubTrigger = TriggerItem;

type MenuSubProps = DropdownMenuSubProps & {
	/** When specified, touch-friendly modal is shown on screens with imprecise pointers (like phones) */
	mobileHeaderTitle?: string;
};
function MenuSub({ mobileHeaderTitle, ...props }: MenuSubProps) {
	const { children, ...rest } = props;

	const noCursor = useMedia(`(pointer: coarse)`);
	const showModalInsteadOfMenu = noCursor && Boolean(mobileHeaderTitle);
	const [isOpen, setIsOpen] = React.useState(false);
	const openModal = React.useCallback(() => setIsOpen(true), []);
	const closeModal = React.useCallback(() => setIsOpen(false), []);

	const modalItemsValue = React.useMemo<MobileModalData>(
		() => ({
			showModalInsteadOfMenu,
			modalTitle: mobileHeaderTitle ?? "🥷🏿",
			isOpen,
			openModal,
			closeModal,
		}),
		[showModalInsteadOfMenu, mobileHeaderTitle, isOpen, openModal, closeModal],
	);

	if (showModalInsteadOfMenu)
		return <MobileModalContext.Provider value={modalItemsValue}>{children}</MobileModalContext.Provider>;

	return <DropdownMenuPrimitive.Sub {...rest}>{children}</DropdownMenuPrimitive.Sub>;
}
Menu.Sub = MenuSub;

const SubContent = React.forwardRef<HTMLDivElement, DropdownMenuSubContentProps>(
	function SubContent(props, forwardedRef) {
		const { children, className, sideOffset, ...rest } = props;
		const { showModalInsteadOfMenu, modalTitle, isOpen, closeModal } = React.useContext(MobileModalContext);

		if (showModalInsteadOfMenu)
			return (
				//TODO: check if ref is needed here with latest radix changes
				<Modal isOpen={isOpen} onClose={closeModal}>
					<Modal.Header onClose={closeModal}>
						<Modal.Title>{modalTitle}</Modal.Title>
					</Modal.Header>
					<Modal.Body className={styles.mobile}>{children}</Modal.Body>
				</Modal>
			);

		return (
			<Menu.Portal>
				<DropdownMenuPrimitive.SubContent
					{...rest}
					className={cx(styles.content, className)}
					sideOffset={sideOffset ?? 1}
					ref={forwardedRef}
				>
					{children}
				</DropdownMenuPrimitive.SubContent>
			</Menu.Portal>
		);
	},
);
Menu.SubContent = SubContent;

// TODO: Shouldn't this at least extend DropdownMenuItemProps? onSelect isn't available here
type ItemLinkProps = (
	| (LinkProps & AdditionalItemProps)
	| (React.HTMLProps<HTMLAnchorElement> & React.AnchorHTMLAttributes<HTMLAnchorElement> & AdditionalItemProps)
) & { showExternalIcon?: boolean };

/** Since react-router has a "to" prop we took advantage of the fact that an {a}-tag does not have this prop.
 * If we specify the "to" prop this component will behave as a react-router link. Otherwise it will behave as
 * an {a}-tag.
 */
function ItemLink(props: ItemLinkProps) {
	const { className, icon, iconProps, showExternalIcon, ...rest } =
		"icon" in props ? props : { icon: undefined, iconProps: undefined, ...props };

	const { showModalInsteadOfMenu, closeModal } = React.useContext(MobileModalContext);

	const itemContent =
		"to" in rest ? (
			<Link {...rest} className={cx(styles.item, className)}>
				{generateItem({ ...rest, icon, iconProps })}
				{showExternalIcon && (
					<FontAwesomeIcon fixedWidth icon={faArrowUpRightFromSquare} className={styles.externalLinkIcon} />
				)}
			</Link>
		) : (
			<a {...rest} className={cx(styles.item, className, { [styles.itemDisabled]: rest.disabled })}>
				{generateItem({ ...rest, icon, iconProps })}
				{!props.download && showExternalIcon !== false && (
					<FontAwesomeIcon fixedWidth icon={faArrowUpRightFromSquare} className={styles.externalLinkIcon} />
				)}
			</a>
		);

	if (showModalInsteadOfMenu) return <>{itemContent}</>;

	return (
		<DropdownMenuPrimitive.Item asChild onSelect={closeModal}>
			{itemContent}
		</DropdownMenuPrimitive.Item>
	);
}
Menu.ItemLink = ItemLink;

function generateItem(
	props: DropdownMenuItemProps | DropdownMenuPrimitive.DropdownMenuSubTriggerProps | ItemLinkProps,
) {
	if ("icon" in props && props.icon) {
		const { children, icon, iconProps } = props;

		return (
			<>
				{icon && <FontAwesomeIcon fixedWidth icon={icon} className={styles.itemIcon} {...iconProps} />}
				<span className={styles.itemText}>{children}</span>
			</>
		);
	} else if ("user" in props || "account" in props || "team" in props || "emoji" in props) {
		const { children, ...rest } = props;

		let user, account, team, emoji;

		if ("user" in rest) {
			user = rest.user;
		} else if ("account" in rest) {
			account = rest.account;
		} else if ("team" in rest) {
			team = rest.team;
		} else if ("emoji" in rest) {
			emoji = rest.emoji;
		}

		return (
			<>
				{user && <Avatar className={styles.itemAvatar} user={user} />}
				{account && <AccountAvatar className={styles.itemAvatar} account={account} />}
				{team && <OldAvatar circle className={styles.itemAvatar} team={team} />}
				{emoji && (
					<div className={styles.itemEmoji}>
						<Emoji size={14} emoji={emoji} native></Emoji>
					</div>
				)}
				<span className={styles.itemText}>{children}</span>
			</>
		);
	} else {
		const { children } = props;

		return <span className={styles.itemText}>{children}</span>;
	}
}

type CheckboxItemProps = Omit<DropdownMenuCheckboxItemProps, "checked" | "onCheckedChange"> & {
	closeOnSelect?: boolean;
	checked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
};

function CheckboxItem(props: CheckboxItemProps) {
	const { children, className, onSelect, closeOnSelect, checked, onCheckedChange, ...rest } = props;

	const { showModalInsteadOfMenu, closeModal } = React.useContext(MobileModalContext);

	const onSelectAndConsiderClosing = React.useCallback(
		(event: Event) => {
			if (!closeOnSelect) event.preventDefault();

			return onSelect?.(event);
		},
		[onSelect, closeOnSelect],
	);

	const itemContent = (
		<>
			<Checkbox checked={checked} className={cx(styles.itemIcon, styles.checkbox)} />
			<span className={styles.itemText}>{children}</span>
		</>
	);

	const onCheckedChangeWithCheckedState = React.useCallback(
		(checked: boolean | "indeterminate") => {
			return onCheckedChange?.(checked === true);
		},
		[onCheckedChange],
	);

	if (showModalInsteadOfMenu) {
		return (
			<div
				{...rest}
				onClick={event => {
					onCheckedChange?.(!checked);
					onSelect?.(event.nativeEvent);
					if (closeOnSelect) closeModal();
				}}
				className={cx(styles.item, className)}
			>
				{itemContent}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.CheckboxItem
			{...rest}
			checked={checked}
			onCheckedChange={onCheckedChangeWithCheckedState}
			className={cx(styles.item, className)}
			onSelect={onSelectAndConsiderClosing}
		>
			{itemContent}
		</DropdownMenuPrimitive.CheckboxItem>
	);
}
Menu.CheckboxItem = CheckboxItem;

const RadioGroupContext = React.createContext<{
	currentValue: string | undefined;
	changeValue: (value: string | undefined) => void;
}>({
	currentValue: undefined,
	changeValue: noop,
});
RadioGroupContext.displayName = "RadioGroupContext";

function RadioGroup(props: DropdownMenuRadioGroupProps) {
	const { children, value, onValueChange, ...rest } = props;

	const { showModalInsteadOfMenu } = React.useContext(MobileModalContext);
	const contextValue = React.useMemo(
		() => ({ currentValue: value, changeValue: onValueChange ?? noop }),
		[value, onValueChange],
	);

	if (showModalInsteadOfMenu) {
		return (
			<RadioGroupContext.Provider value={contextValue}>
				<div {...rest}>{children}</div>
			</RadioGroupContext.Provider>
		);
	}

	return (
		<DropdownMenuPrimitive.RadioGroup {...rest} value={value} onValueChange={onValueChange}>
			{children}
		</DropdownMenuPrimitive.RadioGroup>
	);
}
Menu.RadioGroup = RadioGroup;

function RadioItem(props: WithAdditionalItemProps<DropdownMenuRadioItemProps>) {
	const { className, icon, iconProps, ...rest } =
		"icon" in props ? props : { icon: undefined, iconProps: undefined, ...props };
	const { disabled } = rest;

	const { showModalInsteadOfMenu, closeModal } = React.useContext(MobileModalContext);
	const { currentValue, changeValue } = React.useContext(RadioGroupContext);

	if (showModalInsteadOfMenu) {
		const { onSelect, value, ...others } = rest;

		return (
			<div
				{...others}
				onClick={event => {
					if (disabled) return;
					changeValue(value);
					onSelect?.(event.nativeEvent);
					closeModal();
				}}
				className={cx(styles.item, className, { [styles.itemDisabled]: disabled })}
			>
				{generateItem({ ...rest, icon, iconProps })}
				{currentValue === value && (
					<span>
						<FontAwesomeIcon fixedWidth icon={faCheck} className={styles.radioItemIcon} />
					</span>
				)}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.RadioItem {...rest} className={cx(styles.item, className)}>
			{generateItem({ ...rest, icon, iconProps })}
			<DropdownMenuPrimitive.ItemIndicator>
				<FontAwesomeIcon fixedWidth icon={faCheck} className={styles.radioItemIcon} />
			</DropdownMenuPrimitive.ItemIndicator>
		</DropdownMenuPrimitive.RadioItem>
	);
}
Menu.RadioItem = RadioItem;

function SwitchItem(props: MenuItemProps & CheckboxItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
	const { className, onSelect, closeOnSelect, checked, onCheckedChange, icon, iconProps, ...rest } =
		"icon" in props ? props : { icon: undefined, iconProps: undefined, ...props };

	const { showModalInsteadOfMenu, closeModal } = React.useContext(MobileModalContext);

	const onSelectAndConsiderClosing = React.useCallback(
		(event: Event) => {
			if (!closeOnSelect) event.preventDefault();

			return onSelect?.(event);
		},
		[onSelect, closeOnSelect],
	);

	const itemContent = (
		<>
			{generateItem({ ...rest, icon, iconProps })}
			<Switch checked={checked} size="small" disabled={props.disabled} />
		</>
	);

	const onCheckedChangeWithCheckedState = React.useCallback(
		(checked: boolean | "indeterminate") => {
			return onCheckedChange?.(checked === true);
		},
		[onCheckedChange],
	);

	if (showModalInsteadOfMenu) {
		return (
			<div
				{...rest}
				ref={ref}
				onClick={event => {
					if (!rest.disabled) {
						onCheckedChange?.(!checked);
						onSelect?.(event.nativeEvent);
						if (closeOnSelect) closeModal();
					}
				}}
				className={cx(styles.item, styles.switchItem, className, { [styles.itemDisabled]: rest.disabled })}
			>
				{itemContent}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.CheckboxItem
			{...rest}
			ref={ref}
			checked={checked}
			onCheckedChange={onCheckedChangeWithCheckedState}
			className={cx(styles.item, styles.switchItem, className, { [styles.itemDisabled]: rest.disabled })}
			onSelect={onSelectAndConsiderClosing}
		>
			{itemContent}
		</DropdownMenuPrimitive.CheckboxItem>
	);
}
Menu.SwitchItem = React.forwardRef(SwitchItem);

function Label(props: DropdownMenuLabelProps) {
	const { children, className, ...rest } = props;
	const { showModalInsteadOfMenu } = React.useContext(MobileModalContext);

	if (showModalInsteadOfMenu) {
		return (
			<div {...rest} className={cx(styles.label, className)}>
				{children}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.Label {...rest} className={cx(styles.label, className)}>
			{children}
		</DropdownMenuPrimitive.Label>
	);
}
Menu.Label = Label;

function Separator(props: DropdownMenuSeparatorProps) {
	const { children, className, ...rest } = props;
	const { showModalInsteadOfMenu } = React.useContext(MobileModalContext);

	if (showModalInsteadOfMenu) {
		return (
			<div {...rest} className={cx(styles.separator, className)}>
				{children}
			</div>
		);
	}

	return (
		<DropdownMenuPrimitive.Separator {...rest} className={cx(styles.separator, className)}>
			{children}
		</DropdownMenuPrimitive.Separator>
	);
}
Menu.Separator = Separator;

function Arrow(props: Omit<DropdownMenuArrowProps, "children">) {
	return <DropdownMenuPrimitive.Arrow {...props} className={styles.arrow} />;
}

Menu.Arrow = Arrow;

function LoadingItem({ animationDelayIndex = 0 }: { animationDelayIndex?: number }) {
	return (
		<div
			className={cx(styles.item, styles.loadingItem)}
			style={{ "--animation-delay-index": animationDelayIndex } as React.CSSProperties}
		>
			<div className={styles.itemText}>
				&#8203;{/* Zero-width space to make the line-height kick in since there's no text in here */}
			</div>
		</div>
	);
}
Menu.LoadingItem = LoadingItem;

export default Menu;
